import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap, faChevronDown, faChevronUp, faArrowRight,
  faLayerGroup, faCrown, faQuestionCircle, faEdit, faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { getExamUrl } from '../utils/slugify';

interface Exam {
  id: number;
  name: string;
  description: string;
  is_free_available: boolean;
  question_count?: number;
  badge_image?: string;
}

interface Subcategory {
  subcategory: string | null;
  exams: Exam[];
}

interface Category {
  category: string;
  subcategories: Subcategory[];
}

interface CategorizedExamListProps {
  categories: Category[];
}

const CategorizedExamList: React.FC<CategorizedExamListProps> = ({ categories }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(categories.map((c) => c.category))
  );
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const toggleSubcategory = (category: string, subcategory: string | null) => {
    const key = `${category}:${subcategory || 'null'}`;
    setExpandedSubcategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const goToExam = (exam: Exam, e?: React.MouseEvent) => {
    if (e && (e.target as HTMLElement).closest('.manage-questions-btn')) return;
    navigate(getExamUrl(exam.id, exam.name));
  };

  return (
    <div className="space-y-3">
      {categories.map((category) => {
        const isExpanded = expandedCategories.has(category.category);
        const examCount = category.subcategories.reduce((sum, sub) => sum + sub.exams.length, 0);

        return (
          <section key={category.category} className="space-y-3">
            <button
              type="button"
              onClick={() => toggleCategory(category.category)}
              className="gp-category-header group"
              aria-expanded={isExpanded}
            >
              <div className="flex items-center gap-2.5 flex-1 text-left">
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: 'var(--gp-brand)', color: '#fff' }}
                >
                  <FontAwesomeIcon icon={faLayerGroup} className="text-xs" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>
                    {category.category}
                  </h3>
                  <p className="text-[11px] gp-text-muted">
                    {examCount} {examCount === 1 ? 'exam' : 'exams'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-[11px] font-bold px-2 py-0.5 rounded"
                  style={{ background: 'var(--gp-brand-soft)', color: 'var(--gp-brand)' }}
                >
                  {examCount}
                </span>
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                  className="text-xs gp-text-muted"
                  aria-hidden="true"
                />
              </div>
            </button>

            {isExpanded && (
              <div className="space-y-4 pl-0 sm:pl-2">
                {category.subcategories.map((subcategory) => {
                  const subcategoryKey = `${category.category}:${subcategory.subcategory || 'null'}`;
                  const isSubExpanded =
                    expandedSubcategories.has(subcategoryKey) || expandedSubcategories.size === 0;
                  const hasMultiple = category.subcategories.length > 1;

                  return (
                    <div key={subcategoryKey} className="space-y-3">
                      {hasMultiple && (
                        <button
                          type="button"
                          onClick={() => toggleSubcategory(category.category, subcategory.subcategory)}
                          className="w-full px-3 py-1.5 flex items-center justify-between rounded-md text-sm font-medium transition-colors"
                          style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }}
                          aria-expanded={isSubExpanded}
                        >
                          <span>{subcategory.subcategory || 'General'}</span>
                          <span className="flex items-center gap-2">
                            <span className="text-xs opacity-80">{subcategory.exams.length}</span>
                            <FontAwesomeIcon icon={isSubExpanded ? faChevronUp : faChevronDown} className="text-xs" aria-hidden="true" />
                          </span>
                        </button>
                      )}

                      {isSubExpanded && (
                        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 ${hasMultiple ? 'sm:ml-2' : ''}`}>
                          {subcategory.exams.map((exam) => (
                            <div
                              key={exam.id}
                              role="link"
                              tabIndex={0}
                              onClick={(e) => goToExam(exam, e)}
                              onKeyDown={(e) => e.key === 'Enter' && goToExam(exam)}
                              className="gp-exam-card group cursor-pointer"
                            >
                              <div className="gp-exam-card-badge">
                                {exam.badge_image ? (
                                  <img
                                    src={`/${exam.badge_image}`}
                                    alt=""
                                    className="w-16 h-16 object-contain"
                                    loading="lazy"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="w-14 h-14 rounded-lg flex items-center justify-center"
                                    style={{ background: 'linear-gradient(135deg, var(--gp-brand), #17838d)' }}
                                  >
                                    <FontAwesomeIcon icon={faGraduationCap} className="text-white text-xl" aria-hidden="true" />
                                  </div>
                                )}
                              </div>
                              <div className="gp-exam-card-body">
                                <h4 className="font-semibold text-sm mb-1 line-clamp-2 group-hover:underline" style={{ color: 'var(--gp-ink)' }}>
                                  {exam.name}
                                </h4>
                                {exam.description && (
                                  <p className="text-xs gp-text-muted line-clamp-2 mb-2 flex-1">{exam.description}</p>
                                )}
                                {exam.question_count !== undefined && (
                                  <p className="text-[11px] gp-text-muted flex items-center gap-1 mb-2">
                                    <FontAwesomeIcon icon={faQuestionCircle} aria-hidden="true" />
                                    {exam.question_count} questions
                                  </p>
                                )}
                                <div className="flex items-center justify-between pt-2 border-t mt-auto" style={{ borderColor: 'var(--gp-border)' }}>
                                  {exam.is_free_available ? (
                                    <span className="gp-tag-free">
                                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" aria-hidden="true" />
                                      Free
                                    </span>
                                  ) : (
                                    <span className="gp-tag-premium">
                                      <FontAwesomeIcon icon={faCrown} className="mr-1" aria-hidden="true" />
                                      Premium
                                    </span>
                                  )}
                                  {user && (
                                    <Link
                                      to={`/admin/questions/${exam.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="manage-questions-btn text-xs font-semibold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                      style={{ background: 'var(--gp-brand-soft)', color: 'var(--gp-brand)' }}
                                    >
                                      <FontAwesomeIcon icon={faEdit} aria-hidden="true" />
                                    </Link>
                                  )}
                                  <FontAwesomeIcon
                                    icon={faArrowRight}
                                    className="text-xs ml-auto group-hover:translate-x-0.5 transition-transform"
                                    style={{ color: 'var(--gp-brand)' }}
                                    aria-hidden="true"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default CategorizedExamList;
